// src/store/trainingSlice.js
import { createSlice } from '@reduxjs/toolkit';
import { toISODate, makeISO, humanLabel } from '../utils/schedule';
import { scheduleTrainingPlan } from '../api/trainingApi';

function pad2(n){ return String(n).padStart(2, '0'); }

const initialState = {
  completedPlan: null,
  scheduler: { open: false, plan: null, dateISO: '', timeHHmm: '' },
  lastScheduled: null, // { planId, scheduledTime, at }
};

const trainingSlice = createSlice({
  name: 'training',
  initialState,
  reducers: {
    setCompletedPlan(state, action) { state.completedPlan = action.payload; },
    clearCompletedPlan(state) { state.completedPlan = null; },

    openScheduleModal(state, action) {
      const plan = action.payload;
      state.scheduler.open = true;
      state.scheduler.plan = plan;
      if (plan?.scheduledTime) {
        const d = new Date(plan.scheduledTime);
        state.scheduler.dateISO = toISODate(d);
        state.scheduler.timeHHmm = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
      } else {
        state.scheduler.dateISO = '';
        state.scheduler.timeHHmm = '';
      }
    },
    closeScheduleModal(state) {
      state.scheduler.open = false;
      state.scheduler.plan = null;
      state.scheduler.dateISO = '';
      state.scheduler.timeHHmm = '';
    },
    setScheduleDate(state, action) { state.scheduler.dateISO = action.payload; },
    setScheduleTime(state, action) { state.scheduler.timeHHmm = action.payload; },

    scheduleSaved(state, action) {
      const { planId, scheduledTime } = action.payload;
      state.lastScheduled = { planId, scheduledTime, at: Date.now() };
    },
  },
});

export const {
  setCompletedPlan,
  clearCompletedPlan,
  openScheduleModal,
  closeScheduleModal,
  setScheduleDate,
  setScheduleTime,
  scheduleSaved,
} = trainingSlice.actions;

// ✅ Thunk returns details for toasting
export const saveScheduleThunk = () => async (dispatch, getState) => {
  const { scheduler: { plan, dateISO, timeHHmm } } = getState().training;

  // validations (component button is disabled, but double-check)
  const requestedISO = makeISO(dateISO, timeHHmm);
  if (!plan || !requestedISO) {
    const e = new Error('Pick date & time'); e.code = 'MISSING'; throw e;
  }
  if (new Date(requestedISO) <= new Date()) {
    const e = new Error('Invalid time'); e.code = 'PAST'; throw e;
  }

  // API
  const res = await scheduleTrainingPlan(plan.id, requestedISO);
  const serverISO = res?.plan?.scheduled_time || res?.plan?.scheduledTime || requestedISO;
  const title = res?.plan?.title || plan.title || 'Training';

  // state updates
  dispatch(scheduleSaved({ planId: plan.id, scheduledTime: serverISO }));
  dispatch(closeScheduleModal());

  // return info for toast
  return { planId: plan.id, scheduledTime: serverISO, title, label: humanLabel(dateISO, timeHHmm) };
};

export default trainingSlice.reducer;
